// Authed REST client for the NightSwarm plane. Same-origin in production
// (CloudFront proxies /machines, /sessions, /agents, /schedules, /jobs,
// /teams, /review, /push, /settings, /auth to API Gateway).

import { getTenantId } from "./tenant";

const BASE = import.meta.env.VITE_AUTH_API_URL || "";
const JWT_KEY = "nightswarm_jwt";

function jwt(): string | null {
  try {
    return localStorage.getItem(JWT_KEY);
  } catch {
    return null;
  }
}

// The Swarm tab's enroll snippet embeds the signed-in user's own JWT so
// the command is copy-paste-complete (field report 2026-06-11: the
// "<your token>" placeholder made enrolling a second machine a dead end).
export function enrollToken(): string | null {
  return jwt();
}

/** Error carrying the HTTP status and the plane's `{error}` code (e.g.
 *  "machine_offline" on 409) so the UI can show targeted hints. */
export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

/** Phase 10: 402 from the plane is ALWAYS "upgrade" — the enforcement
 *  layer answers `{error:"plan_limit", limit, max?, plan?}` on every gate
 *  (machine/agent/schedule counts, goal/loop modes, team creation).
 *  Create flows catch this and render an inline upgrade note → /pricing. */
export class PlanLimitError extends ApiError {
  /** Which gate tripped: "machines" | "agents" | "schedules" | "modes" |
   *  "teams" (absent on older planes that 402 without a body). */
  limit?: string;
  /** The plan's cap, for counted limits (e.g. 3 agents on free). */
  max?: number;
  /** The caller's plan as the plane saw it (e.g. "free"). */
  plan?: string;
  constructor(
    message: string,
    detail: { error?: string; limit?: string; max?: number; plan?: string }
  ) {
    super(message, 402, detail.error ?? "plan_limit");
    this.name = "PlanLimitError";
    this.limit = detail.limit;
    this.max = detail.max;
    this.plan = detail.plan;
  }
}

// ─── Tenant threading (phase 6 §2) ─────────────────────────────────
// The selected tenant rides EVERY call: `?tenant=` on the query string and a
// `tenant` field on JSON bodies (contract §2: "?tenant= (GET) or body
// field"; we send both — they're always the same value, so whichever the
// plane reads wins). The personal tenant is OMITTED entirely: on the plane,
// the "ownerEmail" table ATTRIBUTE holds the tenantId (personal tenant =
// the user's email; team tenant = teamId uuid), and an absent tenant
// resolves to the caller's email — backward compatible with older planes.
// Exempt: /auth (caller-scoped), /teams (tenant-explicit via the path),
// and /checkout (billing is per-user — entitlements live on the caller's
// UsersTable row, never on a tenant).

function tenantExempt(path: string): boolean {
  return (
    path.startsWith("/auth") ||
    path.startsWith("/teams") ||
    path.startsWith("/checkout")
  );
}

function tenantPath(path: string): string {
  const tenant = getTenantId();
  if (!tenant || tenantExempt(path)) {
    return path;
  }
  return `${path}${path.includes("?") ? "&" : "?"}tenant=${encodeURIComponent(tenant)}`;
}

function tenantBody(path: string, body: unknown): unknown {
  const tenant = getTenantId();
  if (!tenant || tenantExempt(path)) {
    return body;
  }
  if (typeof body === "object" && body !== null && !Array.isArray(body)) {
    return { tenant, ...(body as Record<string, unknown>) };
  }
  return body;
}

async function req<T>(
  method: string,
  path: string,
  body?: unknown,
  // thread:false skips the selected-tenant threading entirely — the
  // /enroll/desktop flow (tauri-desktop.md "One-click enrollment") operates
  // on an EXPLICIT tenant (personal pre-flight / picked team) regardless of
  // which tenant the dashboard happens to have selected in localStorage.
  opts: { thread?: boolean } = {}
): Promise<T> {
  const thread = opts.thread !== false;
  const token = jwt();
  if (!token) throw new ApiError("not signed in", 0);
  const payload =
    body !== undefined ? (thread ? tenantBody(path, body) : body) : undefined;
  const res = await fetch(`${BASE}${thread ? tenantPath(path) : path}`, {
    method,
    headers: {
      authorization: `Bearer ${token}`,
      ...(payload !== undefined ? { "content-type": "application/json" } : {}),
    },
    body: payload !== undefined ? JSON.stringify(payload) : undefined,
  });
  if (!res.ok) {
    let detail: { error?: string; limit?: string; max?: number; plan?: string } =
      {};
    try {
      detail = (await res.json()) as typeof detail;
    } catch {
      // non-JSON error body — status alone will have to do
    }
    const code = detail.error;
    const message = `${method} ${path} → ${res.status}${code ? ` (${code})` : ""}`;
    // Phase 10: 402 is the plan-limit status everywhere on the plane.
    if (res.status === 402) throw new PlanLimitError(message, detail);
    throw new ApiError(message, res.status, code);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

async function get<T>(path: string): Promise<T> {
  return req<T>("GET", path);
}

// ─── Capabilities ──────────────────────────────────────────────────

/** Upgraded daemons report skills/agents as {name, description?}. */
export interface NamedCapability {
  name: string;
  description?: string;
}

/** Pre-upgrade daemons sent plain string arrays — accept both shapes. */
export type SkillCapability = NamedCapability | string;
export type AgentCapability = NamedCapability | string;

export interface McpCapability {
  name: string;
  status: string;
  transport?: "stdio" | "http";
  /** Phase-4 scanners report where the server was configured; older daemons
   *  omit both (tolerate the old shape). */
  scope?: "user" | "project" | "local";
  source?: string; // project path for non-user scopes, e.g. "~/dev"
}

/** Normalize a possibly-plain-string capability for display. */
export function asNamed(cap: NamedCapability | string): NamedCapability {
  return typeof cap === "string" ? { name: cap } : cap;
}

export interface MachineCapabilities {
  mcps?: McpCapability[];
  skills?: SkillCapability[];
  agents?: AgentCapability[];
  /** macOS Full Disk Access for the daemon (TCC); false → Mail/Messages
   *  agents fail with "authorization denied". Older daemons omit it. */
  fullDiskAccess?: boolean;
}

/** Phase 12: a coding-agent runtime. Agents pick one (or "any"); machines
 *  report which they can serve; sessions/usage carry the one they ran on.
 *  Absent ⇒ "claude" everywhere (pre-phase-12 default). */
export type Runtime = "claude" | "codex" | "any";

/** A machine's served runtimes, with the pre-phase-12 default applied
 *  (absent/empty ⇒ ["claude"]). */
export function machineRuntimes(m: { runtimes?: Runtime[] }): Runtime[] {
  return m.runtimes && m.runtimes.length > 0 ? m.runtimes : ["claude"];
}

// ─── Machines / sessions (phase 1) ─────────────────────────────────

export interface Machine {
  machineId: string;
  name: string;
  status: "online" | "offline";
  lastSeenAt?: string;
  capabilities?: MachineCapabilities;
  /** Phase 9 §A: capacity belongs to the ACCOUNT, not the machine — the
   *  daemon reports its Claude account as seatId (accountUuid, fallback
   *  email, last resort "machine:<id>") + a display label (the account
   *  email). Machines sharing an account share its limits. Older daemons/
   *  planes omit all four. */
  seatId?: string;
  seatLabel?: string;
  /** The seat reported a usage limit (plane SeatsTable flag, resolved onto
   *  machine rows). Stale after 5h at read time (§A) — check client-side
   *  too via seatLimitedAt when present. */
  seatLimited?: boolean;
  seatLimitedAt?: string;
  /** Phase 12 §T7: the coding-agent runtimes this machine can serve, as the
   *  daemon reported them. Absent/empty ⇒ ["claude"] (a pre-phase-12 daemon
   *  serves Claude only). */
  runtimes?: Runtime[];
}

export interface Session {
  sessionId: string;
  machineId: string;
  agent: string;
  cwd?: string;
  status: "starting" | "running" | "ended" | "error";
  startedAt: string;
  endedAt?: string;
  exitCode?: number;
  errorMessage?: string;
  lastSeq?: number;
  /** Phase 5: the session is blocked waiting on a person (stuck prompt or
   *  auto-answer cap). Cleared by the plane when transcript events resume.
   *  Older planes omit it. */
  needsHuman?: boolean;
  /** The stripped screen tail captured when needsHuman fired — shows WHAT
   *  is blocking so the approval bar can render the pending prompt. */
  needsHumanScreen?: string;
  /** Phase 5: cumulative token usage summed from assistant transcript
   *  events. Older planes/sessions omit all four. */
  inputTokens?: number;
  outputTokens?: number;
  cacheReadTokens?: number;
  cacheWriteTokens?: number;
  /** Phase 9 §C: the model/effort the session was dispatched with (override
   *  > agent default > none) — absent when nothing was assigned. */
  model?: string;
  effort?: string;
  /** Phase 9 §B session watchdog: cumulative tokens crossed the tenant's
   *  perSessionMaxTokens. v1 notifies only — the session keeps running. */
  overBudget?: boolean;
  /** Session-autoClose: the resolved value the session was dispatched with
   *  (override > agent default > source default) — true ⇒ the daemon ends
   *  the session once it finishes its turn. Absent when the plane doesn't
   *  surface it (older planes / keep-warm sessions). */
  autoClose?: boolean;
  /** Phase 12 §T8: the coding-agent runtime this session ran on. Absent ⇒
   *  "claude" (pre-phase-12 sessions, and Claude-only tenants). */
  runtime?: Runtime;
  /** Dispatch profiles: the resolved intensity rung the session was
   *  dispatched with — absent (or "standard") when no preamble was
   *  injected. */
  intensity?: string;
}

export interface SessionEvent {
  seq: number;
  ts: string;
  kind: "transcript" | "status";
  payload: string;
}

export async function getMachines(): Promise<Machine[]> {
  const res = await get<{ machines: Machine[] }>("/machines");
  return res.machines ?? [];
}

export async function getMachine(machineId: string): Promise<Machine> {
  return get<Machine>(`/machines/${encodeURIComponent(machineId)}`);
}

/** PERSONAL-tenant machines, ignoring the dashboard's selected team. The
 *  /enroll/desktop pre-flight gates on the personal machine cap (plan
 *  limits apply to personal tenants only — entitlements-read.ts; on the
 *  plane the "ownerEmail" table ATTRIBUTE holds the tenantId and an absent
 *  tenant resolves to the caller's email). */
export async function getPersonalMachines(): Promise<Machine[]> {
  const res = await req<{ machines: Machine[] }>("GET", "/machines", undefined, {
    thread: false,
  });
  return res.machines ?? [];
}

/** DELETE /machines/:id — unenroll (tauri-desktop.md "Entitlement gating +
 *  unenroll" §4). The plane deletes the machine row AND invalidates its
 *  machineToken (the daemon's next WS auth fails; schedules pinned to the
 *  machine stop being servable). Rides the selected tenant by default
 *  (Swarm tab); pass personal:true from the /enroll/desktop paywall, which
 *  always operates on the personal tenant. */
export async function deleteMachine(
  machineId: string,
  opts: { personal?: boolean } = {}
): Promise<void> {
  await req<void>(
    "DELETE",
    `/machines/${encodeURIComponent(machineId)}`,
    undefined,
    { thread: !opts.personal }
  );
}

/** POST /machines/enroll-token — mint the 10-minute SINGLE-USE enroll JWT
 *  for the desktop loopback flow (tauri-desktop.md "One-click enrollment"
 *  §3). The long-lived session JWT must never ride the loopback URL (it
 *  lands in browser history) — this scoped token rides instead, and the
 *  caller must never log it. 402 → PlanLimitError: the mint runs the SAME
 *  machine-cap gate as POST /machines (gating §2 — defense in depth).
 *  tenantId rides when a team was picked (mirrors `nightswarmd enroll
 *  --team`); both spellings are sent while the plane track settles. */
export async function mintEnrollToken(tenantId: string | null): Promise<string> {
  const res = await req<{ token?: string; enrollToken?: string }>(
    "POST",
    "/machines/enroll-token",
    tenantId ? { tenantId, tenant: tenantId } : {},
    { thread: false }
  );
  const token = res.token ?? res.enrollToken;
  if (!token) throw new ApiError("enroll-token mint returned no token", 0);
  return token;
}

export async function getMachineSessions(machineId: string): Promise<Session[]> {
  const res = await get<{ sessions: Session[] }>(
    `/machines/${encodeURIComponent(machineId)}/sessions`
  );
  return res.sessions ?? [];
}

export async function getSession(sessionId: string): Promise<Session> {
  return get<Session>(`/sessions/${encodeURIComponent(sessionId)}`);
}

export async function getSessionEvents(
  sessionId: string,
  since = 0
): Promise<{ events: SessionEvent[]; lastSeq: number }> {
  return get(`/sessions/${encodeURIComponent(sessionId)}/events?since=${since}&limit=500`);
}

/** Forward text into a live (starting/running) session — 202 on delivery.
 *  409 when the session has ended or its machine is offline. */
export async function sendSessionMessage(
  sessionId: string,
  text: string
): Promise<void> {
  await req<void>("POST", `/sessions/${encodeURIComponent(sessionId)}/message`, {
    text,
  });
}

/** Ask the daemon to stop a running session — 202; the session row flips to
 *  "ended" once the daemon confirms. */
export async function stopSession(sessionId: string): Promise<void> {
  await req<void>("POST", `/sessions/${encodeURIComponent(sessionId)}/stop`);
}

// ─── Model + effort assignment (phase 9 §C) ────────────────────────

// Dispatch profiles: xhigh/max added for Claude Code (/effort goes to max);
// codex reasoning tops out at xhigh (the plane clamps max→xhigh for codex
// sessions at dispatch time — see docs/model-routing.md).
export type Effort = "low" | "medium" | "high" | "xhigh" | "max";

/** The named model shorthands every select offers. */
export const MODEL_PRESETS = ["opus", "sonnet", "haiku"] as const;

export const EFFORTS: readonly Effort[] = [
  "low",
  "medium",
  "high",
  "xhigh",
  "max",
];

/** Contract §C: model is one of the presets OR a raw model id matching
 *  this (advanced users) — validated plane-side too. */
export const MODEL_RE = /^[a-z0-9.-]{3,64}$/;

export function isValidModel(model: string): boolean {
  return (
    (MODEL_PRESETS as readonly string[]).includes(model) || MODEL_RE.test(model)
  );
}

// ─── Agents (phase 2 §1) ───────────────────────────────────────────

export interface Agent {
  agentName: string;
  description?: string;
  machineId: string; // pinned machine id, or "any"
  cwd?: string;
  currentSessionId?: string;
  createdAt?: string;
  updatedAt?: string;
  /** Phase 5: warm agents get a session auto-started when their pinned
   *  machine's daemon registers. Older planes omit it. */
  warm?: boolean;
  /** Phase 9 §C agent defaults — schedules/jobs may override per run.
   *  Absent = inherit Claude Code's own defaults. */
  defaultModel?: string;
  defaultEffort?: Effort;
  /** Session-autoClose §MCP+Web agent default — schedules/jobs may override
   *  per run. true ⇒ end the session once it finishes its turn (fresh next
   *  run). Absent = fall to the source default (scheduled/run-now → close;
   *  ask_agent → keep warm). */
  defaultAutoClose?: boolean;
  /** Phase 12 §T6: the coding-agent runtime this agent runs on. Absent ⇒
   *  "claude" (the plane defaults it for pre-phase-12 agents). */
  runtime?: Runtime;
}

export interface AgentInput {
  description?: string;
  machineId: string;
  cwd?: string;
  warm?: boolean;
  /** Phase 9 §C — PUT /agents/{name} replaces the row, so omitting these
   *  clears a stored default back to inherit. */
  defaultModel?: string;
  defaultEffort?: Effort;
  /** Session-autoClose — PUT /agents/{name} replaces the row, so omitting
   *  this clears the stored default back to the source default. */
  defaultAutoClose?: boolean;
  /** Phase 12 §T6: "claude" | "codex" | "any". Unlike the §9C/§autoClose
   *  defaults, omitting runtime leaves the stored value untouched (the plane
   *  only SETs it when present). T9 wires the editor UI. */
  runtime?: Runtime;
}

export async function getAgents(): Promise<Agent[]> {
  const res = await get<{ agents: Agent[] }>("/agents");
  return res.agents ?? [];
}

export async function putAgent(name: string, input: AgentInput): Promise<Agent> {
  return req<Agent>("PUT", `/agents/${encodeURIComponent(name)}`, input);
}

export async function deleteAgent(name: string): Promise<void> {
  await req<void>("DELETE", `/agents/${encodeURIComponent(name)}`);
}

/** The core routing primitive — routes to the agent's live session or
 *  starts a new one. Throws ApiError code "machine_offline" on 409. */
export async function sendAgentMessage(
  name: string,
  text: string
): Promise<{ sessionId: string; routed: "existing" | "started" }> {
  return req("POST", `/agents/${encodeURIComponent(name)}/message`, { text });
}

// ─── Agent instruction files (phase 11) ────────────────────────────
// The plane never touches machine files — GET/PUT round-trip through the
// agent's machine daemon over the existing WebSocket. Tenant scoping rides
// like every other call (`?tenant=` / body field via the threading above);
// the plane resolves agent → tenant → machine and the machine must belong
// to the agent's tenant.

/** Which agent file — an enum on the wire (plane AND daemon validate it;
 *  never a path, so no traversal): "claudemd" = CLAUDE.md (writable),
 *  "settings" = .claude/settings.local.json (read-only from the web in v1 —
 *  permission grants are managed on the machine). */
export type AgentFileKind = "claudemd" | "settings";

/** Contract write cap — the daemon rejects oversize; checked client-side
 *  too so the editor can warn before the round trip. */
export const AGENT_FILE_MAX_BYTES = 32 * 1024;

export interface AgentFile {
  /** Daemon-confirmed file content. Missing files read as "" (ok:true). */
  content: string;
  /** When the plane completed the daemon round trip (ISO). */
  fetchedAt?: string;
}

/** GET /agents/{name}/file?file= — the plane creates a request row, relays
 *  `read_agent_file` to the daemon, and polls up to 10s. Errors surface as
 *  ApiError codes: 409 "machine_offline", 504 "machine_timeout". */
export async function getAgentFile(
  name: string,
  file: AgentFileKind
): Promise<AgentFile> {
  return get<AgentFile>(`/agents/${encodeURIComponent(name)}/file?file=${file}`);
}

/** PUT /agents/{name}/file — CLAUDE.md ONLY (the settings allowlist stays
 *  read-only from the web). Returns the daemon-confirmed post-write content
 *  — callers replace their edit buffer with it. Same 409/504 codes. */
export async function putAgentFile(
  name: string,
  content: string
): Promise<AgentFile> {
  return req<AgentFile>("PUT", `/agents/${encodeURIComponent(name)}/file`, {
    file: "claudemd",
    content,
  });
}

// ─── Schedules (phase 2 §2) ────────────────────────────────────────

/** Phase 6 §4: how the daemon delivers the prompt — typed as-is (message,
 *  the default), as `/goal <prompt>`, or as `/loop [interval] <prompt>`.
 *  Claude Code owns the semantics; the plane just carries it through. */
export type RunMode = "message" | "goal" | "loop";

export interface Schedule {
  scheduleId: string;
  name: string;
  cron: string; // 6-field EventBridge expression, no "cron()" wrapper
  agentName: string;
  prompt: string;
  enabled: boolean;
  timezone?: string;
  createdAt?: string;
  lastFiredAt?: string;
  lastJobId?: string;
  /** Phase 6 §4 — optional for backward compat; absent = "message". */
  mode?: RunMode;
  /** Only meaningful with mode "loop" (e.g. "10m"); absent = self-paced. */
  loopInterval?: string;
  /** Phase 9 §C per-schedule override (override > agent default > none). */
  model?: string;
  effort?: Effort;
  /** Phase 9 §B2: exempt from budget holds (e.g. email triage every 2h all
   *  day). Seat-limit "reached" still holds everything. */
  alwaysRun?: boolean;
  /** Session-autoClose per-schedule override (override > agent default >
   *  source default). Absent = inherit (scheduled fires default to close). */
  autoClose?: boolean;
}

export interface ScheduleInput {
  name: string;
  cron: string;
  agentName: string;
  prompt: string;
  enabled?: boolean;
  timezone?: string;
  /** Phase 6 §4 — omit for the default "message" mode. */
  mode?: RunMode;
  loopInterval?: string;
  /** Phase 9 §C/§B2 — omit to inherit / stay budget-governed. */
  model?: string;
  effort?: Effort;
  alwaysRun?: boolean;
  /** Session-autoClose — omit to inherit (scheduled fires default to close). */
  autoClose?: boolean;
}

export async function getSchedules(): Promise<Schedule[]> {
  const res = await get<{ schedules: Schedule[] }>("/schedules");
  return res.schedules ?? [];
}

export async function createSchedule(
  input: ScheduleInput
): Promise<{ scheduleId: string }> {
  return req("POST", "/schedules", input);
}

export async function updateSchedule(
  scheduleId: string,
  input: Partial<ScheduleInput>
): Promise<void> {
  await req("PUT", `/schedules/${encodeURIComponent(scheduleId)}`, input);
}

export async function deleteSchedule(scheduleId: string): Promise<void> {
  await req("DELETE", `/schedules/${encodeURIComponent(scheduleId)}`);
}

// ─── Jobs (phase 2 §3) ─────────────────────────────────────────────

export type JobState = "queued" | "delivered" | "failed";
export type JobSource = "schedule" | "manual" | "mcp";

/** Phase 9: why a queued job is being held by the dispatch gate —
 *  "budget" (night-window token cap reached, §B) or "seat-limit" (the
 *  target machine's Claude seat reported a usage limit, §A). queue-retry
 *  re-delivers once the gate passes. */
export type HoldReason = "budget" | "seat-limit";

export interface Job {
  jobId: string;
  agentName: string;
  prompt: string;
  source: JobSource;
  scheduleId?: string;
  state: JobState;
  sessionId?: string;
  createdAt: string;
  deliveredAt?: string;
  error?: string;
  attempts?: number;
  /** Phase 6 §4 — optional for backward compat; absent = "message". */
  mode?: RunMode;
  loopInterval?: string;
  /** Phase 9: present on queued jobs the dispatch gate is holding. */
  holdReason?: HoldReason;
  /** Phase 9 §C/§B2 — assignment the job was created with, where set. */
  model?: string;
  effort?: Effort;
  alwaysRun?: boolean;
  /** Session-autoClose per-job override (override > agent default > source
   *  default). Absent = inherit (run-now/run_job default to close). */
  autoClose?: boolean;
}

export async function getJobs(
  opts: { state?: JobState; limit?: number } = {}
): Promise<Job[]> {
  const params = new URLSearchParams();
  if (opts.state) params.set("state", opts.state);
  if (opts.limit) params.set("limit", String(opts.limit));
  const qs = params.toString();
  const res = await get<{ jobs: Job[] }>(`/jobs${qs ? `?${qs}` : ""}`);
  return res.jobs ?? [];
}

/** Dispatches immediately (source "manual"); returns the job — state is
 *  "queued" when the target machine is offline (retried for 24h). Mode,
 *  loopInterval (phase 6 §4), and model/effort (phase 9 §C) are omitted
 *  from the body unless set, keeping the request shape identical to
 *  phase 2 for older planes. */
export async function createJob(
  agentName: string,
  prompt: string,
  opts: {
    mode?: RunMode;
    loopInterval?: string;
    model?: string;
    effort?: Effort;
    autoClose?: boolean;
  } = {}
): Promise<Job> {
  return req("POST", "/jobs", {
    agentName,
    prompt,
    ...(opts.mode && opts.mode !== "message" ? { mode: opts.mode } : {}),
    ...(opts.mode === "loop" && opts.loopInterval
      ? { loopInterval: opts.loopInterval }
      : {}),
    ...(opts.model ? { model: opts.model } : {}),
    ...(opts.effort ? { effort: opts.effort } : {}),
    // Session-autoClose: a boolean override is always expressible — send it
    // when the composer set one (true OR false), so an explicit "keep warm"
    // can override the run-now source default of close.
    ...(opts.autoClose !== undefined ? { autoClose: opts.autoClose } : {}),
  });
}

// ─── Tenant settings / budgets (phase 9 §B) ────────────────────────

/** TenantSettingsTable row (contract §B). The plane's GET /settings
 *  (settings/handler.ts settingsView) always returns every field, with
 *  EXPLICIT `null` for unset caps — the types carry that null honestly so
 *  callers don't `String(null)` it into an input. Absent fields only occur
 *  on older/flat planes; plane defaults are window 22:00–08:00
 *  America/New_York, pauseWhenSeatLimited true, notifyOnBudget true. */
export interface TenantSettings {
  /** Nightly cap: input+output tokens, cache excluded. null/absent = no cap. */
  tokensPerNight?: number | null;
  /** "HH:MM" tenant-local; the window may span midnight. */
  nightWindowStart?: string;
  nightWindowEnd?: string;
  timezone?: string;
  pauseWhenSeatLimited?: boolean;
  /** Session watchdog cap — v1 notifies only, never kills the session.
   *  null/absent = no cap. */
  perSessionMaxTokens?: number | null;
  notifyOnBudget?: boolean;
}

/** Optional §B2 usage rollup some planes return alongside GET /settings.
 *  Bound tolerantly: as of this track the plane handler (Track P) had not
 *  landed, so the Tonight budget bar prefers `usage.windowTokens` when a
 *  plane provides it and otherwise computes an approximation from the
 *  session rows the dashboard already has (see lib/budget.ts). */
export interface SettingsUsage {
  /** Input+output tokens used in the current night window. */
  windowTokens?: number;
  /** Phase 12: the window's `windowTokens` split per coding-agent service
   *  (swarm_status.budget.byService / token_usage.byRuntime on the MCP
   *  side). Absent on Claude-only tenants and older planes — the UI then
   *  shows the total alone (or approximates the split from session rows). */
  byService?: Partial<Record<Runtime, number>>;
}

export interface TenantSettingsRes {
  settings: TenantSettings;
  usage?: SettingsUsage;
}

/** Raw GET /settings body — tolerate a flat settings doc or a
 *  `{settings, usage}` envelope while Track P's exact shape settles. The
 *  per-service split may arrive as `usage.byService` or `usage.byRuntime`
 *  (the MCP side names it byRuntime) — both are normalized below. */
interface SettingsRawRes extends Partial<TenantSettings> {
  settings?: TenantSettings;
  usage?: SettingsUsage & { byRuntime?: Partial<Record<Runtime, number>> };
}

/** GET /settings?tenant= (member read). Older planes have no route —
 *  callers hide the budget surface on any load error. */
export async function getSettings(): Promise<TenantSettingsRes> {
  const res = await get<SettingsRawRes>("/settings");
  const s = res.settings ?? res;
  // Normalize the per-service split: accept byService OR the MCP-side
  // byRuntime spelling, whichever the plane sent.
  const u = res.usage;
  const usage: SettingsUsage | undefined = u
    ? { windowTokens: u.windowTokens, byService: u.byService ?? u.byRuntime }
    : undefined;
  return {
    settings: {
      // Canonicalize "no cap" to null (the plane sends explicit nulls;
      // older/flat planes may omit the field entirely).
      tokensPerNight: s.tokensPerNight ?? null,
      nightWindowStart: s.nightWindowStart,
      nightWindowEnd: s.nightWindowEnd,
      timezone: s.timezone,
      pauseWhenSeatLimited: s.pauseWhenSeatLimited,
      perSessionMaxTokens: s.perSessionMaxTokens ?? null,
      notifyOnBudget: s.notifyOnBudget,
    },
    usage,
  };
}

/** PUT /settings (any member — teams are small and trusted, contract §B).
 *  The plane is a PARTIAL update (settings/handler.ts): omitted fields stay
 *  untouched and a cap clears ONLY on an explicit null. So callers pass the
 *  whole settings doc they intend (including fields they don't edit, e.g.
 *  notifyOnBudget), and the cap fields are ALWAYS sent — number to set,
 *  null to clear — otherwise emptying a cap input would silently leave the
 *  old cap in place. */
export async function putSettings(settings: TenantSettings): Promise<void> {
  await req<void>("PUT", "/settings", {
    tokensPerNight: settings.tokensPerNight ?? null,
    ...(settings.nightWindowStart
      ? { nightWindowStart: settings.nightWindowStart }
      : {}),
    ...(settings.nightWindowEnd
      ? { nightWindowEnd: settings.nightWindowEnd }
      : {}),
    ...(settings.timezone ? { timezone: settings.timezone } : {}),
    ...(settings.pauseWhenSeatLimited !== undefined
      ? { pauseWhenSeatLimited: settings.pauseWhenSeatLimited }
      : {}),
    perSessionMaxTokens: settings.perSessionMaxTokens ?? null,
    ...(settings.notifyOnBudget !== undefined
      ? { notifyOnBudget: settings.notifyOnBudget }
      : {}),
  });
}

// ─── Web Push (phase 4 §B/§D) ──────────────────────────────────────

/** GET /push/vapid-public-key — tolerate `{publicKey}` JSON (and a couple of
 *  likely key names) or a raw base64url string body. */
export async function getVapidPublicKey(): Promise<string> {
  const token = jwt();
  if (!token) throw new ApiError("not signed in", 0);
  // Raw fetch (tolerant body parsing) — still rides the tenant like every
  // other call (phase 6 §2).
  const res = await fetch(`${BASE}${tenantPath("/push/vapid-public-key")}`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw new ApiError(`GET /push/vapid-public-key → ${res.status}`, res.status);
  }
  const text = (await res.text()).trim();
  if (!text) throw new ApiError("empty VAPID key response", res.status);
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return text; // raw key body
  }
  if (typeof parsed === "string") return parsed;
  const obj = parsed as { publicKey?: string; vapidPublicKey?: string; key?: string };
  const key = obj.publicKey ?? obj.vapidPublicKey ?? obj.key;
  if (!key) throw new ApiError("unrecognized VAPID key response", res.status);
  return key;
}

/** Upsert this browser's push subscription (keyed server-side by a hash of
 *  its endpoint). */
export async function createPushSubscription(
  subscription: PushSubscriptionJSON
): Promise<void> {
  await req<void>("POST", "/push/subscriptions", { subscription });
}

/** Remove the subscription matching this endpoint. */
export async function deletePushSubscription(endpoint: string): Promise<void> {
  await req<void>("DELETE", "/push/subscriptions", { endpoint });
}

// ─── Teams (phase 6 §1) ────────────────────────────────────────────
// A team IS a tenant: its teamId is the tenantId the plane stores in the
// "ownerEmail" table attribute (which, despite its name, holds a tenantId —
// personal tenant = the user's email; team tenant = teamId uuid). Teams
// routes are addressed by teamId in the path, so the selected-tenant
// threading above skips them.

export interface TeamMember {
  email: string;
  role: "owner" | "member";
  /** "invited" until the invitee accepts on sign-in; older rows omit it. */
  status?: "invited" | "member";
  invitedBy?: string;
  joinedAt?: string;
}

export interface Team {
  teamId: string;
  name: string;
  createdBy?: string;
  createdAt?: string;
  /** Freeform key/values (githubOrg, linearWorkspace, conventions, …) —
   *  agents read these via their prompts; the plane never interprets them. */
  settings?: Record<string, string>;
  /** The caller's role, when the plane includes it on list responses. */
  role?: "owner" | "member";
  /** The caller's membership status on list responses — "invited" until
   *  they accept (contract §1); older planes/rows omit it. */
  status?: "invited" | "member";
}

/** GET /teams/{id} — team + members + settings (contract §1). */
export interface TeamDetail extends Team {
  members: TeamMember[];
  /** The caller's own role/status, when the plane includes them. */
  myRole?: "owner" | "member";
  myStatus?: "invited" | "member";
}

/** Raw GET /teams/{id} body — tolerate both a flattened team and a nested
 *  `{team, members}` envelope while track A's exact shape settles. */
interface TeamDetailRes extends Partial<Team> {
  team?: Team & { members?: TeamMember[] };
  members?: TeamMember[];
  settings?: Record<string, string>;
  myRole?: "owner" | "member";
  myStatus?: "invited" | "member";
}

export async function getTeams(): Promise<Team[]> {
  const res = await get<{ teams: Team[] }>("/teams");
  return res.teams ?? [];
}

/** POST /teams {name} — the creator becomes owner. */
export async function createTeam(name: string): Promise<{ teamId: string }> {
  const res = await req<{ teamId?: string } & Partial<Team>>("POST", "/teams", {
    name,
  });
  const teamId = res.teamId;
  if (!teamId) throw new ApiError("create team returned no teamId", 0);
  return { teamId };
}

export async function getTeam(teamId: string): Promise<TeamDetail> {
  const res = await get<TeamDetailRes>(`/teams/${encodeURIComponent(teamId)}`);
  const team = res.team ?? res;
  return {
    teamId: team.teamId ?? teamId,
    name: team.name ?? "",
    createdBy: team.createdBy,
    createdAt: team.createdAt,
    settings: team.settings ?? res.settings ?? {},
    members: res.members ?? res.team?.members ?? [],
    myRole: res.myRole,
    myStatus: res.myStatus,
  };
}

/** PUT /teams/{id}/settings (owner) — replaces the freeform settings map. */
export async function putTeamSettings(
  teamId: string,
  settings: Record<string, string>
): Promise<void> {
  await req<void>("PUT", `/teams/${encodeURIComponent(teamId)}/settings`, {
    settings,
  });
}

/** POST /teams/{id}/members {email, role} (owner). v1 sends no invite
 *  email — the invitee sees the team on next sign-in. */
export async function addTeamMember(
  teamId: string,
  email: string,
  role: "owner" | "member"
): Promise<void> {
  await req<void>("POST", `/teams/${encodeURIComponent(teamId)}/members`, {
    email,
    role,
  });
}

/** POST /teams/{id}/accept — flip MY "invited" membership row to "member"
 *  (contract §1: the invitee sees the team on next sign-in and accepts).
 *  The plane 409s with code "already_member" when there's nothing to
 *  accept — callers may treat that as success. */
export async function acceptTeamInvite(teamId: string): Promise<void> {
  await req<void>("POST", `/teams/${encodeURIComponent(teamId)}/accept`);
}

/** DELETE /teams/{id}/members/{email} — owner removes anyone; a member may
 *  remove themself (leave). */
export async function removeTeamMember(
  teamId: string,
  email: string
): Promise<void> {
  await req<void>(
    "DELETE",
    `/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(email)}`
  );
}

// ─── Morning review (phase 6 §3) ───────────────────────────────────

export interface ReviewSession {
  sessionId: string;
  agent?: string;
  machineId?: string;
  /** Some digest builders may resolve the machine name plane-side. */
  machine?: string;
  status?: string;
  needsHuman?: boolean;
  inputTokens?: number;
  outputTokens?: number;
  firstLine?: string;
  lastAssistantLine?: string;
}

export interface ReviewJob {
  jobId?: string;
  agentName?: string;
  state?: string;
  source?: string;
  scheduleName?: string;
  error?: string;
  createdAt?: string;
  sessionId?: string;
}

export interface ReviewTotals {
  sessions?: number;
  inputTokens?: number;
  outputTokens?: number;
  failedJobs?: number;
}

export interface ReviewAck {
  email: string;
  date?: string;
  reviewedAt?: string;
}

export interface ReviewDigest {
  /** The review date the plane keys acks by (ReviewAcksTable SK is
   *  date#email); fall back to today when absent. */
  date?: string;
  since?: string;
  sessions: ReviewSession[];
  jobs: ReviewJob[];
  totals?: ReviewTotals;
  reviewedBy: ReviewAck[];
}

/** Raw GET /review body — every field optional (older planes 404 this route
 *  entirely; the caller hides the section on any error). */
interface ReviewRes {
  date?: string;
  since?: string;
  sessions?: ReviewSession[];
  jobs?: ReviewJob[];
  totals?: ReviewTotals;
  reviewedBy?: (ReviewAck | string)[];
}

/** GET /review — last-night digest for the selected tenant (`?tenant=` rides
 *  via the threading above). `since` defaults to 18h ago plane-side. */
export async function getReview(since?: string): Promise<ReviewDigest> {
  const res = await get<ReviewRes>(
    `/review${since ? `?since=${encodeURIComponent(since)}` : ""}`
  );
  return {
    date: res.date,
    since: res.since,
    sessions: res.sessions ?? [],
    jobs: res.jobs ?? [],
    totals: res.totals,
    // Tolerate a plain email array or {email,...} objects.
    reviewedBy: (res.reviewedBy ?? []).map((r) =>
      typeof r === "string" ? { email: r } : r
    ),
  };
}

/** POST /review/ack {tenant, date} — "<email> reviewed <date>". The tenant
 *  body field rides via the threading above (omitted = personal). */
export async function ackReview(date: string): Promise<void> {
  await req<void>("POST", "/review/ack", { date });
}

// ─── Auth (caller-scoped, tenant-exempt) ───────────────────────────

/** /auth/me shape the enroll pre-flight cares about. useAuth owns the boot
 *  validation; this re-fetch exists so /enroll/desktop's paywall can see an
 *  upgrade land (checkout completes in ANOTHER tab — the cached useAuth
 *  user would stay "free" forever). */
export interface Me {
  email: string;
  ownedEntitlements: string[];
}

export async function getMe(): Promise<Me> {
  return get<Me>("/auth/me");
}

// ─── Billing (phase 10 §C) ─────────────────────────────────────────

export type BillingInterval = "monthly" | "yearly";

/** POST /checkout {entitlementId, interval, seats?} — creates a Stripe
 *  Checkout session for a recurring plan and returns its hosted URL (the
 *  caller redirects to it). Caller-scoped like /auth (tenant-exempt above):
 *  the granted entitlement lands on the buyer's UsersTable row. `seats`
 *  only applies to "teams" (plane validates min 3 / max 100; quantity = 1
 *  otherwise). Stripe returns the user to SITE_URL/?purchase=success or
 *  ?purchase=cancelled. */
export async function createCheckout(
  entitlementId: "pro" | "teams",
  interval: BillingInterval,
  seats?: number
): Promise<{ url: string }> {
  return req("POST", "/checkout", {
    entitlementId,
    interval,
    ...(seats !== undefined ? { seats } : {}),
  });
}
